import { NativeBiometric } from "@capgo/capacitor-native-biometric";
import { Preferences } from "@capacitor/preferences";

/**
 * Servicio de Autenticación Biométrica
 * Maneja toda la lógica de biometría para la aplicación
 */

const BIOMETRIC_CREDENTIALS_KEY = "biometric_credentials";
const BIOMETRIC_ENABLED_KEY = "biometric_enabled";
const BIOMETRIC_LAST_USER_KEY = "biometric_last_user";

/**
 * Verifica si la autenticación biométrica está disponible en el dispositivo
 * @returns {Promise<{isAvailable: boolean, biometryType: string}>}
 */
export const checkBiometricAvailable = async () => {
  try {
    const result = await NativeBiometric.isAvailable();
    return {
      isAvailable: result.isAvailable,
      biometryType: result.biometryType, // 0 = None, 1 = TouchID, 2 = FaceID, 3 = Fingerprint
    };
  } catch (error) {
    console.error("Error checking biometric availability:", error);
    return {
      isAvailable: false,
      biometryType: 0,
    };
  }
};

/**
 * Obtiene el nombre legible del tipo de biometría
 * @param {number} biometryType
 * @returns {string}
 */
export const getBiometryTypeName = (biometryType) => {
  switch (biometryType) {
    case 1:
      return "Touch ID";
    case 2:
      return "Face ID";
    case 3:
      return "Huella Digital";
    default:
      return "Biometría";
  }
};

/**
 * Guarda las credenciales del usuario de forma segura usando biometría
 * @param {string} dni - DNI del usuario
 * @param {string} password - Contraseña del usuario
 * @returns {Promise<boolean>}
 */
export const saveBiometricCredentials = async (dni, password) => {
  try {
    // Verificar que la biometría esté disponible
    const { isAvailable } = await checkBiometricAvailable();
    if (!isAvailable) {
      throw new Error("Biometría no disponible");
    }

    // Guardar credenciales en el almacenamiento seguro nativo (Keychain/Keystore)
    await NativeBiometric.setCredentials({
      username: dni,
      password: password,
      server: "portal_dim_app",
    });

    // Guardar configuración en Preferences
    await Preferences.set({
      key: BIOMETRIC_ENABLED_KEY,
      value: "true",
    });

    await Preferences.set({
      key: BIOMETRIC_LAST_USER_KEY,
      value: dni,
    });

    return true;
  } catch (error) {
    console.error("Error saving biometric credentials:", error);
    throw error;
  }
};

/**
 * Obtiene las credenciales guardadas usando autenticación biométrica
 * @param {string} reason - Razón para solicitar la autenticación
 * @returns {Promise<{dni: string, password: string}>}
 */
export const getBiometricCredentials = async (
  reason = "Inicia sesión con tu huella digital"
) => {
  try {
    // Verificar autenticación biométrica
    await NativeBiometric.verifyIdentity({
      reason: reason,
      title: "Autenticación",
      subtitle: "Verifica tu identidad",
      description: "Usa tu biometría para acceder",
    });

    // Si la verificación fue exitosa, obtener credenciales
    const credentials = await NativeBiometric.getCredentials({
      server: "portal_dim_app",
    });

    if (!credentials || !credentials.username || !credentials.password) {
      throw new Error("No se encontraron credenciales guardadas");
    }

    return {
      dni: credentials.username,
      password: credentials.password,
    };
  } catch (error) {
    console.error("Error getting biometric credentials:", error);
    throw error;
  }
};

/**
 * Elimina las credenciales biométricas guardadas
 * @returns {Promise<boolean>}
 */
export const deleteBiometricCredentials = async () => {
  try {
    // Eliminar credenciales del almacenamiento seguro
    await NativeBiometric.deleteCredentials({
      server: "portal_dim_app",
    });

    // Limpiar preferencias
    await Preferences.remove({ key: BIOMETRIC_ENABLED_KEY });
    await Preferences.remove({ key: BIOMETRIC_LAST_USER_KEY });

    return true;
  } catch (error) {
    console.error("Error deleting biometric credentials:", error);
    throw error;
  }
};

/**
 * Verifica si el usuario tiene la autenticación biométrica habilitada
 * @returns {Promise<boolean>}
 */
export const isBiometricEnabled = async () => {
  try {
    const result = await Preferences.get({ key: BIOMETRIC_ENABLED_KEY });
    return result.value === "true";
  } catch (error) {
    console.error("Error checking if biometric is enabled:", error);
    return false;
  }
};

/**
 * Obtiene el último usuario que usó biometría
 * @returns {Promise<string|null>}
 */
export const getLastBiometricUser = async () => {
  try {
    const result = await Preferences.get({ key: BIOMETRIC_LAST_USER_KEY });
    return result.value || null;
  } catch (error) {
    console.error("Error getting last biometric user:", error);
    return null;
  }
};

/**
 * Solicita autenticación biométrica simple (sin recuperar credenciales)
 * Útil para verificar identidad antes de acciones sensibles
 * @param {string} reason - Razón de la autenticación
 * @returns {Promise<boolean>}
 */
export const authenticateWithBiometric = async (
  reason = "Verifica tu identidad"
) => {
  try {
    await NativeBiometric.verifyIdentity({
      reason: reason,
      title: "Autenticación Requerida",
      subtitle: "Verifica tu identidad",
      description: "Usa tu biometría para continuar",
    });
    return true;
  } catch (error) {
    console.error("Error authenticating with biometric:", error);
    return false;
  }
};

